import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import MusteriModal from './components/MusteriModal';

interface Customer {
  id: number;
  ad_soyad: string;
  telefon: string;
  email: string;
  notlar: string;
  rezervasyon_sayisi?: number;
  son_rezervasyon?: string;
  toplam_eur?: number;
  toplam_usd?: number;
  toplam_tl?: number;
}

export default function MusterilerPage() {
  useAuthGuard();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [arama, setArama] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [cRes, rRes] = await Promise.all([
      supabase.from('customers').select('*').order('ad_soyad'),
      supabase.from('reservations').select('musteri_adi,gelis_tarihi,ucret,para_birimi,durum').neq('durum', 'İptal Edildi'),
    ]);
    if (cRes.error) { setError(cRes.error.message); setLoading(false); return; }

    const reservations = rRes.data || [];
    const enriched = (cRes.data || []).map((c: Customer) => {
      const rez = reservations.filter(r => r.musteri_adi === c.ad_soyad);
      const toplamEUR = rez.filter(r => r.para_birimi === 'EUR').reduce((s: number, r: { ucret: number }) => s + Number(r.ucret), 0);
      const toplamUSD = rez.filter(r => r.para_birimi === 'USD').reduce((s: number, r: { ucret: number }) => s + Number(r.ucret), 0);
      const toplamTL = rez.filter(r => r.para_birimi === 'TL').reduce((s: number, r: { ucret: number }) => s + Number(r.ucret), 0);
      const sonRez = rez.sort((a: { gelis_tarihi: string }, b: { gelis_tarihi: string }) => b.gelis_tarihi.localeCompare(a.gelis_tarihi))[0];
      return { ...c, rezervasyon_sayisi: rez.length, son_rezervasyon: sonRez?.gelis_tarihi || '-', toplam_eur: toplamEUR, toplam_usd: toplamUSD, toplam_tl: toplamTL };
    });
    setCustomers(enriched);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!arama) return customers;
    const q = arama.toLowerCase();
    return customers.filter(c =>
      c.ad_soyad?.toLowerCase().includes(q) || c.telefon?.includes(q) || c.email?.toLowerCase().includes(q)
    );
  }, [customers, arama]);

  const showSuccess = (msg: string) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const handleSave = async (data: Omit<Customer, 'id'>) => {
    setSaving(true);
    if (editCustomer) {
      const { error: err } = await supabase.from('customers').update(data).eq('id', editCustomer.id);
      if (err) { setError(err.message); setSaving(false); return; }
      setCustomers(prev => prev.map(c => c.id === editCustomer.id ? { ...c, ...data } : c));
      showSuccess('Müşteri güncellendi.');
    } else {
      const { data: inserted, error: err } = await supabase.from('customers').insert(data).select().maybeSingle();
      if (err) { setError(err.message); setSaving(false); return; }
      if (inserted) setCustomers(prev => [...prev, { ...inserted, rezervasyon_sayisi: 0, son_rezervasyon: '-' }]);
      showSuccess('Müşteri eklendi.');
    }
    setSaving(false);
    setShowModal(false);
    setEditCustomer(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const idToDelete = deleteConfirm.id;
    setDeleting(true);
    setDeleteConfirm(null);

    const { data, error } = await supabase.functions.invoke('manage-data', {
      body: { action: 'delete', table: 'customers', id: idToDelete },
    });

    setDeleting(false);

    if (error || !data?.success) {
      setError('Silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
      await fetchData();
      return;
    }
    setCustomers(prev => prev.filter(c => c.id !== idToDelete));
    showSuccess('Müşteri silindi.');
  };

  return (
    <AdminLayout>
      {showModal && (
        <MusteriModal
          customer={editCustomer}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditCustomer(null); }}
          saving={saving}
        />
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-vip-dark border border-red-500/20 rounded-xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-white text-center mb-2">Müşteriyi Sil</h3>
            <p className="text-sm text-gray-400 text-center mb-5"><strong className="text-white">{deleteConfirm.ad_soyad}</strong></p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white transition-colors whitespace-nowrap">İptal</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50 whitespace-nowrap">
                {deleting ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">Müşteriler</h2>
          <p className="text-sm text-gray-400 mt-1">Müşteri veritabanı ve rezervasyon geçmişi</p>
        </div>
        <button onClick={() => { setEditCustomer(null); setShowModal(true); }}
          className="px-4 py-2 bg-vip-gold text-vip-black text-sm font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap">
          <i className="ri-add-line mr-1"></i>Yeni Müşteri
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-gray-500 hover:text-white"><i className="ri-close-line"></i></button>
        </div>
      )}
      {successMsg && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-check-line text-emerald-400"></i>
          <p className="text-sm text-emerald-400">{successMsg}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-gray-500 text-sm"></i>
          </div>
          <input type="text" placeholder="Müşteri adı, telefon veya e-posta ara..." value={arama} onChange={e => setArama(e.target.value)}
            className="w-full bg-vip-dark border border-vip-gold/15 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-vip-gold/40 transition-colors" />
        </div>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-md px-3 py-2 text-sm text-gray-400">
          <span className="text-vip-gold font-semibold">{filtered.length}</span> müşteri
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-14 bg-vip-dark rounded-lg animate-pulse"></div>)}</div>
      ) : (
        <>
          <div className="hidden md:block bg-vip-dark border border-vip-gold/10 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-vip-gold/10 bg-vip-black/50">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Ad Soyad</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Telefon</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">E-posta</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Rezervasyon</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Son Transfer</th>
                    <th className="text-right py-3 px-4 text-gray-400 font-medium">Toplam Ödeme</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Notlar</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} className="border-b border-vip-gold/5 hover:bg-white/5 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-vip-gold/10 flex items-center justify-center"><i className="ri-user-line text-vip-gold text-xs"></i></div>
                          <span className="text-white font-medium">{c.ad_soyad}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-300 text-xs">{c.telefon}</td>
                      <td className="py-3 px-4 text-gray-300 text-xs">{c.email}</td>
                      <td className="py-3 px-4 text-center"><span className="text-white font-semibold">{c.rezervasyon_sayisi}</span></td>
                      <td className="py-3 px-4 text-gray-300">{c.son_rezervasyon}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="space-y-0.5">
                          {(c.toplam_eur || 0) > 0 && <p className="text-xs text-vip-gold">{c.toplam_eur} €</p>}
                          {(c.toplam_usd || 0) > 0 && <p className="text-xs text-blue-400">{c.toplam_usd} $</p>}
                          {(c.toplam_tl || 0) > 0 && <p className="text-xs text-emerald-400">{Number(c.toplam_tl).toLocaleString('tr-TR')} ₺</p>}
                          {!c.toplam_eur && !c.toplam_usd && !c.toplam_tl && <p className="text-xs text-gray-500">-</p>}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-500 text-xs max-w-[150px] truncate">{c.notlar}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => { setEditCustomer(c); setShowModal(true); }} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center"><i className="ri-edit-line text-xs"></i></button>
                          <button onClick={() => setDeleteConfirm(c)} className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center"><i className="ri-delete-bin-line text-xs"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="md:hidden grid grid-cols-1 gap-3">
            {filtered.map(c => (
              <div key={c.id} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-vip-gold/10 flex items-center justify-center"><i className="ri-user-line text-vip-gold text-xs"></i></div>
                    <div><p className="text-sm font-medium text-white">{c.ad_soyad}</p><p className="text-xs text-gray-500">{c.telefon}</p></div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditCustomer(c); setShowModal(true); }} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center"><i className="ri-edit-line text-xs"></i></button>
                    <button onClick={() => setDeleteConfirm(c)} className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center"><i className="ri-delete-bin-line text-xs"></i></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-vip-black/50 rounded-md p-2"><p className="text-gray-500 mb-0.5">Rezervasyon</p><p className="text-white font-semibold">{c.rezervasyon_sayisi}</p></div>
                  <div className="bg-vip-black/50 rounded-md p-2"><p className="text-gray-500 mb-0.5">Son Transfer</p><p className="text-gray-300">{c.son_rezervasyon}</p></div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {!loading && filtered.length === 0 && (
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-8 text-center">
          <p className="text-sm text-gray-400">Aramaya uygun müşteri bulunamadı.</p>
        </div>
      )}
    </AdminLayout>
  );
}